import { Form, Input, Modal, Radio, Typography, message as antMessage } from 'antd';
import { useState } from 'react';
import { api } from '../lib/api';

const { Text } = Typography;

type Level = 'info' | 'warning' | 'urgent';

const QUICK_REPLIES: { label: string; text: string; level: Level }[] = [
  { label: 'You\'re going live in 30 seconds', text: "You're going live in 30 seconds — get ready.", level: 'urgent' },
  { label: 'Please check your mic', text: 'Please check your microphone — we can\'t hear you clearly.', level: 'warning' },
  { label: 'Please move closer to the camera', text: 'Please move closer to the camera.', level: 'warning' },
  { label: 'Stand by, we\'ll be with you shortly', text: 'Stand by — we\'ll be with you shortly.', level: 'info' },
  { label: 'Thank you, that was great', text: 'Thank you, that was great!', level: 'info' },
];

export function SendMessageModal({
  open,
  recipient,
  onSend,
  onClose,
}: {
  open: boolean;
  /** Display name shown in the title. Use 'Everyone' for broadcast. */
  recipient: string;
  /** What to do with the validated payload. Resolves on success. */
  onSend: (payload: { text: string; level: Level }) => Promise<void>;
  onClose: () => void;
}) {
  const [form] = Form.useForm<{ text: string; level: Level }>();
  const [busy, setBusy] = useState(false);

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      setBusy(true);
      await onSend({ text: values.text.trim(), level: values.level });
      antMessage.success('Message sent');
      form.resetFields();
      onClose();
    } catch (err) {
      const e = err as { error?: string; errorFields?: unknown };
      if (e.error) antMessage.error(e.error);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      title={`Send message to ${recipient}`}
      open={open}
      onCancel={() => {
        form.resetFields();
        onClose();
      }}
      onOk={handleOk}
      confirmLoading={busy}
      okText="Send"
    >
      <Form form={form} layout="vertical" initialValues={{ level: 'info' }}>
        <Form.Item
          name="text"
          label="Message"
          rules={[{ required: true, message: 'Type something to send' }]}
        >
          <Input.TextArea autoSize={{ minRows: 2, maxRows: 5 }} autoFocus />
        </Form.Item>
        <Form.Item name="level" label="How to show it">
          <Radio.Group>
            <Radio value="info">Info (subtle toast)</Radio>
            <Radio value="warning">Warning (sticky toast)</Radio>
            <Radio value="urgent">Urgent (modal — they must click OK)</Radio>
          </Radio.Group>
        </Form.Item>
        <Text type="secondary" style={{ fontSize: 12 }}>
          Quick replies:
        </Text>
        <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {QUICK_REPLIES.map((qr) => (
            <a
              key={qr.label}
              onClick={() =>
                form.setFieldsValue({ text: qr.text, level: qr.level })
              }
              style={{
                fontSize: 12,
                padding: '2px 8px',
                background: '#fafbfc',
                border: '1px solid #e5eaf0',
                borderRadius: 999,
              }}
            >
              {qr.label}
            </a>
          ))}
        </div>
      </Form>
    </Modal>
  );
}

export async function sendMessageToSpeaker(
  eventId: string,
  eventSpeakerId: string,
  payload: { text: string; level: Level },
): Promise<void> {
  await api(`/api/events/${eventId}/speakers/${eventSpeakerId}/message`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function broadcastMessageToEvent(
  eventId: string,
  payload: { text: string; level: Level },
): Promise<void> {
  await api(`/api/events/${eventId}/message`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}
